import { useState } from "react";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import ModalPortal from "@/components/ModalPortal";

/**
 * Admin-modal för att sätta/rensa personnummer och ID06-nummer på en anställd.
 * Personnumret lagras krypterat i backend; UI:t visar bara maskerad version
 * i listor. Endast PDF-exporten innehåller det fullständiga numret.
 */
export default function PersonnummerModal({ user, onClose, onSaved }) {
  const [pnr, setPnr] = useState("");
  const [id06, setId06] = useState(user?.id06_number || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      if (pnr.trim()) payload.personnummer = pnr.trim();
      payload.id06_number = id06.trim();
      const r = await api.patch(`/personalliggare/users/${user.user_id}`, payload);
      toast.success("Uppgifter sparade");
      onSaved?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  const clearPnr = async () => {
    if (!window.confirm(`Ta bort personnummer för ${user.name}?`)) return;
    setSaving(true);
    try {
      const r = await api.patch(`/personalliggare/users/${user.user_id}`, {
        personnummer: "",
      });
      toast.success("Personnummer borttaget");
      onSaved?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte ta bort");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal>
      <div
        data-testid="personnummer-modal"
        className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
        onClick={() => !saving && onClose()}
      >
        <div
          className="bg-surface border border-app rounded-2xl p-6 w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl">Personalliggare-uppgifter</h2>
            <button
              onClick={() => !saving && onClose()}
              className="text-text-muted hover:text-text-primary"
              aria-label="Stäng"
            >
              <X size={20} />
            </button>
          </div>
          <p className="text-sm text-text-muted mt-2">
            För <strong className="text-text-primary">{user.name}</strong>.
            Numret sparas krypterat och visas maskerat i listor. Endast PDF-exporten
            innehåller det fullständiga personnumret.
          </p>

          {user.has_personnummer && (
            <div
              className="mt-4 tk-pill tk-pill-active inline-flex items-center gap-2"
              data-testid="personnummer-current"
            >
              Nuvarande: <span className="font-mono">{user.personnummer_masked}</span>
            </div>
          )}

          <form onSubmit={submit} className="mt-4 space-y-3">
            <div>
              <label className="text-xs text-text-muted">
                {user.has_personnummer ? "Nytt personnummer (lämna tomt för att behålla)" : "Personnummer"}
              </label>
              <input
                data-testid="personnummer-input"
                type="text"
                inputMode="numeric"
                placeholder="ÅÅÅÅMMDD-NNNN eller ÅÅMMDD-NNNN"
                value={pnr}
                onChange={(e) => setPnr(e.target.value)}
                className="tk-input w-full font-mono"
              />
            </div>
            <div>
              <label className="text-xs text-text-muted">ID06-nummer (valfritt)</label>
              <input
                data-testid="id06-input"
                type="text"
                maxLength={40}
                value={id06}
                onChange={(e) => setId06(e.target.value)}
                className="tk-input w-full font-mono"
              />
            </div>
            <div className="flex gap-2 pt-2">
              {user.has_personnummer && (
                <button
                  type="button"
                  onClick={clearPnr}
                  disabled={saving}
                  data-testid="clear-personnummer-button"
                  className="tk-btn-outline text-rust text-xs"
                >
                  Ta bort personnr
                </button>
              )}
              <div className="flex-1" />
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="tk-btn-outline"
              >
                Avbryt
              </button>
              <button
                type="submit"
                data-testid="save-personnummer-button"
                disabled={saving || (!pnr.trim() && (id06 || "") === (user.id06_number || ""))}
                className="tk-btn-primary"
              >
                {saving ? "Sparar…" : "Spara"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

// Re-export API base for downstream test hooks
export { API };
